import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  SidebarPinStateProvider,
  sidebarConfig,
} from '@backstage/core-components';

/**
 * Viewport width at or below which the global Backstage sidebar switches to
 * the mobile bottom-bar layout (same chrome as the default xs breakpoint).
 *
 * Chosen so wide data tables (e.g. Release Checklist) get nearly full
 * horizontal room on 13–16" laptops; only ultra-wide monitors keep the
 * persistent left drawer.
 */
export const COMPACT_LAYOUT_MAX_WIDTH_PX = 2500;

const PIN_STORAGE_KEY = '@backstage/core/sidebar-pin-state';

function readPinState(): boolean {
  try {
    const raw = localStorage.getItem(PIN_STORAGE_KEY);
    if (raw === null) return true;
    return JSON.parse(raw) === true;
  } catch {
    return true;
  }
}

/**
 * Drop-in replacement for Backstage `SidebarPage` that flips to the mobile
 * bottom nav at {@link COMPACT_LAYOUT_MAX_WIDTH_PX} instead of MUI `xs`
 * (~600px). Desktop left-drawer + content padding-left only apply above that.
 *
 * Does not re-implement `useContent` / skip-link page context — those are
 * internal to the stock SidebarPage and unused by this app's Root chrome.
 */
export const CompactSidebarPage = ({ children }: PropsWithChildren<{}>) => {
  const [isPinned, setIsPinned] = useState(readPinState);
  const isMobile = useMediaQuery(
    `(max-width:${COMPACT_LAYOUT_MAX_WIDTH_PX}px)`,
    { noSsr: true },
  );

  useEffect(() => {
    try {
      localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify(isPinned));
    } catch {
      // ignore quota / private mode
    }
  }, [isPinned]);

  const toggleSidebarPinState = useCallback(
    () => setIsPinned(prev => !prev),
    [],
  );

  const paddingLeft =
    !isMobile && isPinned
      ? sidebarConfig.drawerWidthOpen
      : !isMobile
      ? sidebarConfig.drawerWidthClosed
      : 0;
  const paddingBottom = isMobile ? sidebarConfig.mobileSidebarHeight : 0;

  return (
    <SidebarPinStateProvider
      value={{
        isPinned,
        toggleSidebarPinState,
        isMobile,
      }}
    >
      <Box
        sx={{
          width: '100%',
          transition: 'padding-left 0.1s ease-out',
          isolation: 'isolate',
          paddingLeft: `${paddingLeft}px`,
          paddingBottom: `${paddingBottom}px`,
          '@media print': {
            padding: '0 !important',
          },
        }}
      >
        {children}
      </Box>
    </SidebarPinStateProvider>
  );
};
